import { FC } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { ParamsProps } from 'src/app/common/components/ParamsProvider';
import PruFilter, { PruFilterItemDef, PruFilterItemType } from 'src/app/common/components/PruTable/PruFilter';
import { useLang } from 'src/app/i18n';
import { useRegistrationListingPage } from './registration-listing-page.hook';
import { RegistrationList } from './components/registration-list.component';
import { fetchLicenseExamExamOptions } from '../../../network/license-exam-crud';
import { ModulePermissionProps } from 'src/app/common/module/types';
import { RegistrationStatesFilterEnum } from '../../../enum/license-exam.enum';
import { Box } from '@mui/material';
import { getConfigurations } from 'src/app/common/utils';
import { get } from 'lodash';

type RegistrationListingPageProps = ParamsProps & ModulePermissionProps;

export const RegistrationListingPage: FC<RegistrationListingPageProps> = ({
  initialParams,
  onChangeQueryParams,
  enableUpdate,
}) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const Translation = (id: string) => intl.formatMessage({ id });
  const locale = useLang();
  const config = getConfigurations();
  const enablePaymentInfo = get(config, 'Recruitment.enableLicenseExamPayment', false);

  const {
    filterState,
    registrationList,
    isLoading,
    enableApproval,
    examCodeOpts,
    examNameOpts,
    setFilterState,
    refreshData,
    onSort,
    handleTemplateChange,
  } = useRegistrationListingPage({
    initialParams,
    onChangeQueryParams,
  });

  // Sort examCodeOpts and examNameOpts in ascending alphabetical order
  const sortedExamCodeOpts = [...(examCodeOpts || [])].sort((a, b) => a.displayName.localeCompare(b.displayName));
  const sortedExamNameOpts = [...(examNameOpts || [])].sort((a, b) => a.displayName.localeCompare(b.displayName));

  return (
    <Box className="auto-table-height">
      <PruFilter
        title={Translation('recruitment.exam.registration.filter')}
        itemDef={[
          {
            type: PruFilterItemType.SYNC_MULTIPLE_AUTOCOMPLETE,
            style: { width: 200 },
            field: 'examCodes',
            initialValue: filterState.examCodes,
            displayName: Translation('recruitment.exam.examCode'),
            choices: sortedExamCodeOpts,
          },
          {
            type: PruFilterItemType.ASYNC_DROPDOWN,
            style: { width: 180 },
            field: 'examType',
            initialValue: filterState.examType,
            displayName: Translation('recruitment.exam.examType'),
            fetchList: async () => {
              const res = await fetchLicenseExamExamOptions('EXAM_TYPE', { lang: locale }, dispatch);
              return res.map((item) => ({
                displayName: item || '-',
                value: item || '-',
              }));
            },
          },
          {
            type: PruFilterItemType.SYNC_MULTIPLE_AUTOCOMPLETE,
            style: { width: 180 },
            field: 'examNames',
            initialValue: filterState.examNames,
            displayName: Translation('recruitment.exam.examName'),
            choices: sortedExamNameOpts,
          },
          {
            type: PruFilterItemType.DATE_RANGE,
            fieldFrom: 'examDateFrom',
            fieldTo: 'examDateTo',
            initialDateFrom: filterState.examDateFrom,
            initialDateTo: filterState.examDateTo,
            displayName: Translation('recruitment.exam.examDate'),
          },
          {
            type: PruFilterItemType.FREE_TEXT,
            style: { width: 200 },
            field: 'examCentre',
            initialValue: filterState.examCentre,
            displayName: Translation('recruitment.exam.examCentre'),
          },
          {
            type: PruFilterItemType.MULTIPLE_DROPDOWN,
            style: { width: 180 },
            field: 'registrationApprovalStates',
            initialValue: filterState.registrationApprovalStates,
            displayName: Translation('recruitment.exam.registration.status'),
            choices: Object.entries(RegistrationStatesFilterEnum).map(([key, value]) => {
              return {
                displayName: Translation(`recruitment.exam.registration.status.${value}`),
                value: key,
              };
            }),
          },
          ...(enablePaymentInfo
            ? [
                {
                  type: PruFilterItemType.FREE_TEXT,
                  style: { width: 200 },
                  field: 'orNumbers',
                  initialValue: filterState.orNumbers,
                  displayName: Translation('recruitment.exam.registration.orNumber'),
                } as PruFilterItemDef,
              ]
            : []),
          {
            type: PruFilterItemType.FREE_TEXT,
            style: { width: 200 },
            field: 'registrationIds',
            initialValue: filterState.registrationIds,
            displayName: Translation('recruitment.exam.registration.id'),
          },
          {
            type: PruFilterItemType.FREE_TEXT,
            style: { width: 200 },
            field: 'applicantIds',
            initialValue: filterState.applicantIds,
            displayName: Translation('recruitment.exam.registration.applicantId'),
          },
          //PHKL custom - s
          {
            type: PruFilterItemType.DROPDOWN,
            style: { width: 180 },
            field: 'isAgentExam',
            initialValue: filterState.isAgentExam,
            displayName: Translation('recruitment.exam.registration.isAgentExam'),
            choices: [
              { displayName: Translation('app.select.all'), value: '' },
              { displayName: Translation('recruitment.exam.registration.isAgentExam.select.no'), value: 'N' },
              { displayName: Translation('recruitment.exam.registration.isAgentExam.select.yes'), value: 'Y' },
            ],
          },
          {
            type: PruFilterItemType.FREE_TEXT,
            style: { width: 200 },
            field: 'agentCode',
            initialValue: filterState.agentCode,
            displayName: Translation('recruitment.exam.registration.agentCode'),
          },
          //PHKL custom - e
          {
            type: PruFilterItemType.FREE_TEXT,
            style: { width: 200 },
            field: 'firstnames',
            initialValue: filterState.firstnames,
            displayName: Translation('recruitment.exam.registration.firstName'),
          },
          {
            type: PruFilterItemType.FREE_TEXT,
            style: { width: 200 },
            field: 'lastnames',
            initialValue: filterState.lastnames,
            displayName: Translation('recruitment.exam.registration.lastName'),
          },
        ]}
        onChangeFilter={(data) =>
          setFilterState({
            ...filterState,
            examCodes: data.examCodes,
            examType: data.examType,
            examNames: data.examNames,
            examDateFrom: data.examDateFrom,
            examDateTo: data.examDateTo,
            examCentre: data.examCentre,
            registrationApprovalStates: data.registrationApprovalStates,
            orNumbers: data.orNumbers,
            registrationIds: data.registrationIds,
            applicantIds: data.applicantIds,
            //PHKL custom - s
            isAgentExam: data.isAgentExam,
            agentCode: data.agentCode,
            //PHKL custom - e
            firstnames: data.firstnames,
            lastnames: data.lastnames,
            result: data.result,
          })
        }
        fetchData={refreshData}
      />
      <RegistrationList
        isLoading={isLoading}
        registrationList={registrationList}
        onRefresh={refreshData}
        onChangePage={(page, rowsPerPage) => {
          setFilterState({
            ...filterState,
            page,
            limit: rowsPerPage,
            lang: locale,
          });
          refreshData();
        }}
        onTemplateChange={handleTemplateChange}
        onSort={onSort}
        formState={filterState}
        enableUpdate={enableUpdate}
        enableApproval={enableApproval}
      />
    </Box>
  );
};
